import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  PhoneCall, PhoneOff, MessageSquare, StickyNote, Calendar, X,
  ChevronLeft, ChevronRight, ChevronDown, Flame, ArrowUpRight, Zap, Radio,
  Voicemail, CheckCircle2, XCircle, Ban, PhoneOutgoing, Save, FileText, Home as HomeIcon,
  Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { OutboundStore } from '../../lib/outboundStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Status-dot color map (small subset of the /outbound page's palette
   — kept local so this file has no cross-page import dependency). */
const STATUS_STYLE = {
  new:            { dot: 'bg-white' },
  attempted:      { dot: 'bg-amber-500' },
  contacted:      { dot: 'bg-sky-500' },
  follow_up:      { dot: 'bg-[hsl(var(--kf-gold))]' },
  appointment:    { dot: 'bg-violet-500' },
  hot:            { dot: 'bg-red-500' },
  not_interested: { dot: 'bg-neutral-500' },
};

const QUICK_RESULTS = [
  { key: 'connected', label: 'Connected',  icon: CheckCircle2, tone: 'emerald' },
  { key: 'left_vm',   label: 'Left VM',    icon: Voicemail,    tone: 'sky' },
  { key: 'no_answer', label: 'No Answer',  icon: PhoneOff,     tone: 'neutral' },
];

const TONE_BTN = {
  emerald: 'bg-emerald-500/15 border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/25',
  sky:     'bg-sky-500/15 border-sky-400/30 text-sky-200 hover:bg-sky-500/25',
  neutral: 'bg-neutral-800 border-white/10 text-neutral-200 hover:bg-neutral-700',
  amber:   'bg-amber-500/15 border-amber-400/30 text-amber-200 hover:bg-amber-500/25',
  red:     'bg-red-500/15 border-red-400/30 text-red-200 hover:bg-red-500/25',
  gold:    'bg-[hsl(var(--kf-gold))]/15 border-[hsl(var(--kf-gold))]/40 text-[hsl(var(--kf-gold))] hover:bg-[hsl(var(--kf-gold))]/25',
};

// Disposition chips shown at the bottom of Focus Mode. These record the
// LATEST call's outcome — the outboundAttempts counter + outboundLastCallAt
// timestamp keep ticking independently (see backend outbound.log_call).
// Attempted / Contacted / Follow-Up were retired per user request; their
// backend enum values remain valid so any historical leads tagged with
// them still deserialise, but LOs can no longer set them from the UI.
const STATUS_OPTIONS = [
  { key: 'appointment',    label: 'Appointment',      icon: Calendar,   tone: 'gold' },
  { key: 'hot',            label: 'HOT · Promote',    icon: Flame,      tone: 'red' },
  { key: 'not_interested', label: 'Not Interested',   icon: XCircle,    tone: 'neutral' },
];

function formatPhoneBig(raw) {
  if (!raw) return '—';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 10) return `${d.slice(0, 3)} · ${d.slice(3, 6)} · ${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) return `+1 · ${d.slice(1, 4)} · ${d.slice(4, 7)} · ${d.slice(7)}`;
  return raw;
}

/**
 * FocusMode — full-screen dialing HUD. Takes over the entire viewport
 * (over the /outbound page) so the LO stops task-switching and just
 * dials the queue one lead at a time.
 *
 * Behavior:
 *   • Big phone number, click-to-dial via tel:
 *   • One-tap outcome buttons (Connected / Left VM / No Answer)
 *   • Notes textarea + optional follow-up datetime
 *   • Status change buttons — HOT auto-promotes
 *   • Next / Skip walks the queue forward
 */
export default function FocusMode({ queue, startIndex = 0, onClose }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(startIndex);
  const [notes, setNotes] = useState('');
  const [followUpAt, setFollowUpAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);       // past notes for this lead
  const [fullLead, setFullLead] = useState(null);   // full doc for the Loan Summary
  const [showSummary, setShowSummary] = useState(false);

  const lead = queue[idx] || null;

  // Pause the OutboundStore background poll while the LO is in the
  // dialer so a slow 50k-row fetch can't queue behind a disposition
  // click. Resumed on unmount — the first catch-up fetch fires there.
  useEffect(() => {
    OutboundStore.pausePolling();
    return () => OutboundStore.resumePolling();
  }, []);

  // Reset per-lead form when navigating.
  useEffect(() => {
    setNotes('');
    setFollowUpAt('');
    setHistory([]);
    setFullLead(null);
    setShowSummary(false);
  }, [idx]);

  // Fetch past notes + full lead doc whenever the current lead changes.
  // Notes give the LO context ("last time I called they were waiting on
  // 2023 taxes"). The full lead unlocks the Loan Summary quick-glance
  // (borrower, property, terms) — that data lives in the `application`
  // blob which the outbound light-projection strips out.
  useEffect(() => {
    if (!lead?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [notesRes, leadRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/notes`, { params: { leadId: lead.id } }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/leads/${lead.id}`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setHistory(Array.isArray(notesRes.data) ? notesRes.data : []);
        if (leadRes.data) setFullLead(leadRes.data);
      } catch { /* silent — the dialer stays usable without history */ }
    })();
    return () => { cancelled = true; };
  }, [lead?.id]);

  // ESC → close. We deliberately DO NOT bind ArrowLeft/ArrowRight to
  // navigate between leads anymore — LOs were losing typed notes when
  // they accidentally hit an arrow key while composing a long note.
  // Lead navigation now requires an explicit button (Next in queue).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!lead) {
    return createPortal(
      <div className="fixed inset-0 z-[90] bg-neutral-950 flex items-center justify-center text-white">
        <div className="text-center">
          <PhoneOutgoing className="h-8 w-8 mx-auto mb-3 text-white/40" />
          <div className="text-[15px] font-semibold">Queue empty</div>
          <button onClick={onClose} className="mt-4 rounded-lg bg-white text-neutral-900 px-4 py-2 text-[12.5px] font-bold">
            Back to Outbound
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  const goNext = () => {
    if (idx < queue.length - 1) setIdx(idx + 1);
    else {
      toast.success('Queue complete');
      onClose();
    }
  };

  const buildPayload = ({ result = null, status = null } = {}) => {
    const p = {};
    if (result) p.result = result;
    if (notes.trim()) p.notes = notes.trim();
    if (followUpAt) p.next_follow_up_at = new Date(followUpAt).toISOString();
    if (status) p.outbound_status = status;
    return p;
  };

  const logAndNext = async (result) => {
    // Optimistic dispositioning (Feb 10, 2026): advance the LO to the
    // next lead IMMEDIATELY and fire the backend write in the
    // background. Previously we awaited the server before advancing,
    // which showed a 30s grey screen when the server was slow (big
    // mailer imports in production put the DB under heavy read
    // pressure every 45s poll). Dialer flow now stays at UI speed.
    //
    // On failure: show a toast and revert the local lead by nudging
    // the store to re-sync. The LO's queue position is kept — they
    // just see "that didn't save, try again" and can retry.
    const currentLeadId = lead.id;
    const payload = buildPayload({ result });
    // Auto-promote status transitions based on result:
    //   connected  → contacted (unless LO also picked a status)
    //   left_vm    → attempted
    //   no_answer  → attempted
    const currentStatus = lead.outboundStatus || 'new';
    let autoNext = null;
    if (result === 'connected' && currentStatus === 'new') autoNext = 'contacted';
    else if ((result === 'left_vm' || result === 'no_answer') && currentStatus === 'new') autoNext = 'attempted';

    goNext();
    toast.success('Call logged');

    // Fire-and-forget — a slow DB write no longer blocks the LO.
    (async () => {
      try {
        const r = await OutboundStore.logCall(currentLeadId, payload);
        if (!r) throw new Error('logCall returned null');
        if (autoNext) {
          await OutboundStore.updateStatus(currentLeadId, autoNext);
        }
      } catch (e) {
        toast.error('Save failed — try again on this lead');
        console.error('[FocusMode] logAndNext backend write failed', e);
      }
    })();
  };

  const applyStatus = async (status) => {
    // Same optimistic pattern as `logAndNext` — the LO moves at UI
    // speed, the write happens in the background.
    const currentLeadId = lead.id;
    const payload = buildPayload({ status });
    const hasNotes = notes.trim() || followUpAt;

    goNext();
    if (status === 'hot') {
      toast.success('🔥 Promoted to CRM — visible under Leads');
    } else {
      toast.success('Status updated');
    }

    (async () => {
      try {
        let r;
        if (hasNotes) {
          r = await OutboundStore.logCall(currentLeadId, payload);
        } else {
          r = await OutboundStore.updateStatus(currentLeadId, status);
        }
        if (!r) throw new Error('status write returned null');
      } catch (e) {
        toast.error('Status save failed — try again on this lead');
        console.error('[FocusMode] applyStatus backend write failed', e);
      }
    })();
  };

  // Save Notes without logging a call attempt. Persists the notes
  // (as a real timeline note), stashes the appointment on the lead,
  // and STAYS on the current record so the LO can keep working it.
  const saveNotesInPlace = async () => {
    if (!notes.trim() && !followUpAt) {
      toast('Add a note or appointment first');
      return;
    }
    setBusy(true);
    try {
      const jobs = [];
      if (notes.trim()) {
        jobs.push(axios.post(`${BACKEND_URL}/api/notes`, {
          leadId: lead.id,
          body: notes.trim(),
        }));
      }
      if (followUpAt) {
        jobs.push(OutboundStore.updateStatus(lead.id, undefined, {
          outboundNextFollowUpAt: new Date(followUpAt).toISOString(),
        }));
      }
      await Promise.all(jobs);
      // Refresh the on-screen history so the newly-saved note shows up
      // right below the textarea.
      try {
        const r = await axios.get(`${BACKEND_URL}/api/notes`, { params: { leadId: lead.id } });
        setHistory(Array.isArray(r.data) ? r.data : []);
      } catch { /* non-fatal */ }
      setNotes('');
      setFollowUpAt('');
      toast.success('Notes saved');
    } catch (e) {
      toast.error('Failed to save notes');
    } finally {
      setBusy(false);
    }
  };

  // Opt Out — the dialer's compliance button. `temp='dnc'` + parking the
  // lead in the "Not Interested" outbound tab keeps them out of every
  // active work surface. IMPORTANT: we DO NOT set `outbound=false` here —
  // if we did, the lead would leak into /api/leads (My Leads / All Leads),
  // which the user's rule forbids for anything except a Hot promotion.
  // Only Opt Out reporting (AdminReports Outcomes tab, filter=DNC) still
  // surfaces them for compliance. Advances the queue after the update.
  const optOutLead = async () => {
    if (!window.confirm(`Opt out ${lead.name} from all outreach?\n\nThey'll be hidden from every list and logged in the Opt Out reporting.`)) return;
    setBusy(true);
    try {
      const noteBody = (notes.trim() ? notes.trim() + '\n\n' : '') + '🚫 Opted out — removed from all outreach';
      await OutboundStore.logCall(lead.id, {
        result: 'do_not_call',
        notes: noteBody,
        outbound_status: 'not_interested',
      });
      // temp='dnc' only — no outbound flip. See comment above.
      await OutboundStore.updateStatus(lead.id, undefined, { temp: 'dnc' });
      toast.success(`${lead.name} opted out`);
      goNext();
    } catch (e) {
      toast.error('Failed to opt out lead');
    } finally {
      setBusy(false);
    }
  };

  // Wrong Number — the phone number was bad. Logs the attempt so the
  // timeline reflects reality, flips `temp='dead'` so the lead is marked
  // as an unreachable write-off, and parks it in the "Not Interested"
  // outbound tab. Same `outbound=true`-preservation rule as Opt Out —
  // ONLY Hot promotion moves a lead into My Leads.
  const wrongNumberLead = async () => {
    if (!window.confirm(`Mark ${lead.name} as Wrong Number?\n\nThey'll be moved out of the active dialer rotation.`)) return;
    setBusy(true);
    try {
      const noteBody = (notes.trim() ? notes.trim() + '\n\n' : '') + '📵 Wrong number — bad phone data';
      await OutboundStore.logCall(lead.id, {
        result: 'wrong_number',
        notes: noteBody,
        outbound_status: 'not_interested',
      });
      // temp='dead' only — no outbound flip. See optOutLead comment.
      await OutboundStore.updateStatus(lead.id, undefined, { temp: 'dead' });
      toast.success(`${lead.name} marked as wrong number`);
      goNext();
    } catch (e) {
      toast.error('Failed to mark wrong number');
    } finally {
      setBusy(false);
    }
  };

  const attempts = lead.outboundAttempts || 0;

  return createPortal(
    <div
      data-testid="focus-mode"
      className="fixed inset-0 z-[90] bg-neutral-950 text-white flex flex-col overflow-y-auto"
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between px-5 py-2 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.24em] text-white/50">
            <Zap className="h-3 w-3 text-[hsl(var(--kf-gold))]" /> Focus mode
          </div>
          <div className="text-[11.5px] tabular-nums text-white/70">
            Lead <b className="text-white">{idx + 1}</b> / <b>{queue.length}</b>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="p-1.5 rounded-lg border border-white/10 hover:bg-white/10 disabled:opacity-30"
            title="Previous (←)"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="focus-skip"
            onClick={goNext}
            className="p-1.5 rounded-lg border border-white/10 hover:bg-white/10"
            title="Skip (→)"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            data-testid="focus-close"
            onClick={onClose}
            className="ml-2 p-1.5 rounded-lg border border-white/10 hover:bg-white/10"
            title="Exit (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Body: [queue rail | dialer content]. The rail lets the LO see
          how many calls are left, which one they're on, and jump
          around the queue without exiting Focus Mode. */}
      <div className="flex-1 flex items-stretch overflow-hidden">
        <QueueRail queue={queue} idx={idx} onPick={setIdx} />
        <div className="flex-1 overflow-y-auto flex items-start justify-center px-6 py-4">
          <div className="w-full max-w-4xl">
          {/* Identity row */}
          <div className="flex items-center gap-3.5 mb-4">
            <div className="relative h-12 w-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-[15px] font-black">
              {lead.initials || '?'}
              {attempts > 0 && (
                <span className="absolute -top-1 -right-1 h-4.5 w-4.5 rounded-full bg-[hsl(var(--kf-gold))] text-neutral-900 text-[10px] font-black flex items-center justify-center border-2 border-neutral-950" style={{ height: '18px', width: '18px' }}>
                  {attempts}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[24px] font-black tracking-tight leading-tight">{lead.name}</div>
              <div className="text-[12px] text-white/50 mt-0.5">
                {lead.outboundCampaign ? <><Radio className="inline-block h-3 w-3 mr-1 mb-0.5" />{lead.outboundCampaign} · </> : null}
                {(lead.city || '—')}
              </div>
            </div>
            <button
              onClick={() => navigate(`/leads/${lead.id}`)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[12px] font-semibold hover:bg-white/10"
            >
              Open profile <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Big phone number + primary action */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 mb-1">Dial</div>
            <div className="text-[42px] sm:text-[52px] font-black tabular-nums leading-none tracking-tight text-white">
              {formatPhoneBig(lead.phone)}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                data-testid="focus-dial"
                href={`tel:${lead.phone || ''}`}
                className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--kf-gold))] px-5 py-3 text-[14px] font-bold text-neutral-950 shadow-[0_10px_30px_-8px_hsl(var(--kf-gold))] hover:brightness-110 transition"
              >
                <PhoneCall className="h-4 w-4" strokeWidth={3} /> Call now
              </a>
              <a
                href={`sms:${lead.phone || ''}`}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-[13px] font-semibold hover:bg-white/10"
              >
                <MessageSquare className="h-4 w-4" /> Text
              </a>
              {/* Copy phone — writes the clean 10-digit number to the
                  clipboard so LOs can paste into tools outside the CRM
                  (RingCentral, personal dialer, CRM search, etc.). */}
              <button
                data-testid="focus-copy-phone"
                type="button"
                onClick={async () => {
                  const digits = (lead.phone || '').replace(/\D/g, '');
                  if (!digits) {
                    toast('No phone number to copy');
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(digits);
                    toast.success(`Copied ${formatPhoneCompact(digits)}`);
                  } catch {
                    // Fallback for browsers that block clipboard access
                    // from async handlers — select-and-execCopy an off-
                    // screen input. Keeps the copy flow working on older
                    // Chromium builds and locked-down enterprise setups.
                    try {
                      const ta = document.createElement('textarea');
                      ta.value = digits;
                      ta.setAttribute('readonly', '');
                      ta.style.position = 'fixed'; ta.style.top = '-9999px';
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                      toast.success(`Copied ${formatPhoneCompact(digits)}`);
                    } catch {
                      toast.error('Could not copy phone number');
                    }
                  }
                }}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-[13px] font-semibold hover:bg-white/10"
                title="Copy phone number to clipboard"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
              {/* Wrong Number — sibling to Opt Out. Same "remove from
                  pipeline entirely" effect (dead + outbound=false) but
                  amber-toned so LOs can eyeball the difference from the
                  red Opt Out. Not a compliance action; just hygiene. */}
              <button
                data-testid="focus-wrong-number"
                onClick={wrongNumberLead}
                disabled={busy}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/15 px-3.5 py-3 text-[12.5px] font-bold text-amber-200 hover:bg-amber-500/25 transition',
                  busy && 'opacity-40 cursor-not-allowed'
                )}
                title="Wrong Number — remove from pipeline entirely"
              >
                <PhoneOff className="h-4 w-4" /> Wrong Number
              </button>
              {/* Opt Out — pulls the lead out of the whole system in
                  one tap. Red, deliberately separated from the other
                  primary actions so it can't be tapped by accident. */}
              <button
                data-testid="focus-opt-out"
                onClick={optOutLead}
                disabled={busy}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/15 px-3.5 py-3 text-[12.5px] font-bold text-red-200 hover:bg-red-500/25 transition',
                  busy && 'opacity-40 cursor-not-allowed'
                )}
                title="Opt out — remove from all outreach"
              >
                <Ban className="h-4 w-4" /> Opt Out
              </button>
              {/* Loan Summary toggle — the collapsed strip keeps the HUD
                  minimal until the client answers, then one tap reveals
                  everything the LO needs in one glance. */}
              <button
                data-testid="focus-summary-toggle"
                onClick={() => setShowSummary((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-[12.5px] font-semibold transition ml-auto',
                  showSummary
                    ? 'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold))]/15 text-[hsl(var(--kf-gold))]'
                    : 'border-white/15 bg-white/5 text-white hover:bg-white/10'
                )}
              >
                <FileText className="h-4 w-4" /> Loan Summary
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showSummary && 'rotate-180')} />
              </button>
            </div>
            {showSummary && (
              <LoanSummary lead={fullLead || lead} />
            )}
          </div>

          {/* Past activity — notes + call history stitched together so
              the LO instantly sees prior context on repeat dials. */}
          {history.length > 0 && (
            <div data-testid="focus-history" className="mt-4">
              <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 mb-1.5 flex items-center gap-1.5">
                <StickyNote className="h-3 w-3" /> Previous activity · {history.length}
              </div>
              <div className="max-h-32 overflow-y-auto pr-1 space-y-1.5 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5">
                {history.slice(0, 20).map((n) => (
                  <div key={n.id} className="text-[12px] leading-snug">
                    <div className="flex items-center gap-2 text-[10.5px] text-white/40">
                      <span className="font-semibold text-white/60">{n.author || 'System'}</span>
                      <span>·</span>
                      <span>{formatNoteWhen(n.createdAt)}</span>
                    </div>
                    <div className="text-white/85 whitespace-pre-wrap">{n.body}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Result quick-tap */}
          <div className="mt-4">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 mb-2">Log outcome</div>
            <div className="grid grid-cols-3 gap-2">
              {QUICK_RESULTS.map((r) => {
                const Icon = r.icon;
                return (
                  <button
                    key={r.key}
                    data-testid={`focus-result-${r.key}`}
                    onClick={() => logAndNext(r.key)}
                    disabled={busy}
                    className={cn(
                      'inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-[13px] font-semibold transition',
                      TONE_BTN[r.tone],
                      busy && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <Icon className="h-4 w-4" /> {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes + Appointment */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40">Notes</div>
                <button
                  data-testid="focus-save-notes"
                  onClick={saveNotesInPlace}
                  disabled={busy || (!notes.trim() && !followUpAt)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] font-bold transition',
                    (busy || (!notes.trim() && !followUpAt))
                      ? 'border-white/10 bg-white/[0.03] text-white/30 cursor-not-allowed'
                      : 'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold))]/15 text-[hsl(var(--kf-gold))] hover:bg-[hsl(var(--kf-gold))]/25'
                  )}
                  title="Save without logging a call"
                >
                  <Save className="h-3 w-3" /> Save notes
                </button>
              </div>
              <textarea
                data-testid="focus-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Anything worth remembering from this call…"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[13px] placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--kf-gold))]/40 resize-none"
              />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 mb-1.5">Appointment</div>
              <input
                data-testid="focus-follow-up"
                type="datetime-local"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 text-[13px] text-white [color-scheme:dark]"
              />
            </div>
          </div>

          {/* Status transitions */}
          <div className="mt-4">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40 mb-2">Or set status</div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {STATUS_OPTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    data-testid={`focus-status-${s.key}`}
                    onClick={() => applyStatus(s.key)}
                    disabled={busy}
                    className={cn(
                      'inline-flex flex-col items-center justify-center gap-0.5 rounded-lg border px-2 py-2 text-[11px] font-semibold transition',
                      TONE_BTN[s.tone],
                      s.key === 'hot' && 'ring-2 ring-[hsl(var(--kf-gold))]/40',
                      busy && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="truncate max-w-full">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="shrink-0 flex items-center justify-between px-5 py-1.5 border-t border-white/10 bg-neutral-950/80 text-[10.5px] text-white/40">
        <div>Shortcut: <kbd className="px-1 rounded bg-white/10 text-white/70">Esc</kbd> exit</div>
        <div className="tabular-nums">Attempt #{attempts + 1}</div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Left rail queue view ──────────────────────────────────────────
   Slim sidebar (~236px) inside Focus Mode showing the full queue at
   a glance. Each item is clickable to jump. Done rows fade + get a
   check; the current row is highlighted in gold; upcoming rows are
   plain white/60. A footer summarizes remaining calls so the LO
   always knows what's left. Collapses on smaller viewports (<lg).  */
function QueueRail({ queue, idx, onPick }) {
  const total = queue.length;
  const remaining = Math.max(0, total - idx); // includes the current lead
  return (
    <aside
      data-testid="focus-queue-rail"
      className="hidden lg:flex shrink-0 w-[236px] flex-col border-r border-white/10 bg-white/[0.02]"
    >
      <div className="px-3.5 pt-4 pb-2 flex items-baseline justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.24em] text-white/40">Call queue</div>
        <div className="text-[11px] tabular-nums text-white/50">
          <b className="text-white">{idx + 1}</b>/{total}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
        {queue.map((l, i) => {
          const done = i < idx;
          const active = i === idx;
          const status = l.outboundStatus || 'new';
          const dotCls = STATUS_STYLE[status]?.dot || 'bg-white';
          return (
            <button
              key={l.id}
              data-testid={`focus-queue-item-${l.id}`}
              onClick={() => onPick(i)}
              className={cn(
                'w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition min-w-0',
                active
                  ? 'bg-[hsl(var(--kf-gold))]/15 border border-[hsl(var(--kf-gold))]/40'
                  : done
                    ? 'opacity-45 hover:opacity-75 hover:bg-white/5'
                    : 'hover:bg-white/5 border border-transparent'
              )}
            >
              <span className={cn(
                'shrink-0 h-6 w-6 rounded-full text-[9.5px] font-black flex items-center justify-center border',
                active ? 'bg-[hsl(var(--kf-gold))] text-neutral-950 border-[hsl(var(--kf-gold))]'
                       : 'bg-white/5 text-white/80 border-white/15'
              )}>
                {done ? '✓' : (l.initials || String(i + 1))}
              </span>
              <div className="min-w-0 flex-1">
                <div className={cn(
                  'text-[11.5px] font-semibold leading-tight truncate',
                  active ? 'text-white' : 'text-white/85'
                )}>{l.name}</div>
                <div className="text-[9.5px] tabular-nums text-white/40 truncate">{formatPhoneCompact(l.phone)}</div>
              </div>
              <span className={cn('shrink-0 h-1.5 w-1.5 rounded-full', dotCls)} />
            </button>
          );
        })}
      </div>
      <div className="border-t border-white/10 px-3.5 py-2.5 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Left</div>
        <div className="text-[15px] font-black tabular-nums text-[hsl(var(--kf-gold))]">
          {remaining}
        </div>
      </div>
    </aside>
  );
}

function formatPhoneCompact(raw) {
  if (!raw) return '—';
  const d = String(raw).replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
}

/* ── Compact Loan Summary strip shown inside the phone-dial card ───
   Kept as a dense two-column grid so it fits below the call-now
   buttons without stretching the HUD or forcing scroll. Data comes
   from the full lead doc (`fullLead`) — falls back to the light
   projection so the LO sees SOMETHING even before /api/leads/{id}
   resolves.                                                        */
function LoanSummary({ lead }) {
  const app = (lead && lead.application) || {};
  const rows = [
    { label: 'Borrower',       value: [app.firstName, app.lastName].filter(Boolean).join(' ') || lead?.name || '—' },
    { label: 'Email',          value: app.email || lead?.email || '—' },
    { label: 'Loan Purpose',   value: prettyPurpose(app.loanPurpose) },
    { label: 'Loan Type',      value: (app.loanType || '—').toString().toUpperCase() },
    { label: 'Purchase Price', value: fmtDollars(app.purchasePrice) },
    { label: 'Loan Amount',    value: fmtDollars(app.loanAmount ?? lead?.estAmount) },
    { label: 'Est. Value',     value: fmtDollars(app.propertyValue) },
    { label: 'Loan Date',      value: fmtLoanDate(app.loanDate) },
    // Term + Rate — surfaced on the HUD so an LO can quote a payoff /
    // break-even in the first 10 seconds of the call. Term renders as
    // "30 yr" / "15 yr"; rate renders with a trailing % and up to 3
    // decimals so a 6.875 doesn't get truncated.
    { label: 'Term',           value: fmtLoanTerm(app.loanTerm) },
    { label: 'Rate',           value: fmtRate(app.loanRate) },
    { label: 'Lender',         value: app.lender || '—' },
    { label: 'Property',       value: formatAddress(app) },
  ];
  return (
    <div data-testid="focus-loan-summary" className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 pt-4 border-t border-white/10">
      {rows.map((r) => (
        <div key={r.label} className="min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.2em] text-white/40">{r.label}</div>
          <div className="text-[12.5px] font-semibold text-white/90 truncate" title={String(r.value)}>{r.value || '—'}</div>
        </div>
      ))}
    </div>
  );
}

/* ── helpers ─────────────────────────────────────────────────────── */
function fmtDollars(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n) || n === 0) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

// Term lives on `application.loanTerm` as a number of YEARS (e.g. 30 or
// 15), occasionally as a string like "30 yr" from a messy CSV. Normalise
// to "30 yr" so the HUD stays consistent — if the input is already a
// non-numeric string we trust it as-is.
function fmtLoanTerm(v) {
  if (v === null || v === undefined || v === '') return '—';
  const digits = String(v).match(/\d+/)?.[0];
  if (!digits) return String(v);
  const n = parseInt(digits, 10);
  if (!n) return '—';
  return `${n} yr`;
}

// Rate = annual interest rate. CSVs sometimes drop the % sign and
// sometimes send `6.875%` as a string. Strip anything non-numeric,
// cap to 3 decimals so `6.875` doesn't truncate.
function fmtRate(v) {
  if (v === null || v === undefined || v === '') return '—';
  const raw = String(v).replace(/[^0-9.\-]/g, '');
  if (!raw) return '—';
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n === 0) return '—';
  // Rates on US mortgages are usually 2-10%. If someone sent 0.06875
  // (decimal form), bump into percent. Keeps the HUD honest without
  // over-engineering.
  const asPct = n < 1 ? n * 100 : n;
  // Up to 3 dp, strip trailing zeros so 6.5 reads "6.5%" not "6.500%".
  return `${parseFloat(asPct.toFixed(3))}%`;
}
// Loan Date lives on `application.loanDate` (set by the outbound importer
// from CSV columns like `loan date`, `funding date`, `close date`). It can
// land as MM/DD/YYYY (mailer + outbound CSVs), ISO 8601 (JSON imports),
// or already-formatted text. We normalise everything to MM/DD/YYYY so the
// dialer's HUD stays skimmable at a glance.
function fmtLoanDate(v) {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v).trim();
  if (!s) return '—';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }
  return s; // Un-parseable string — surface it raw rather than swallow it.
}
function prettyPurpose(p) {
  if (!p) return '—';
  return String(p).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatAddress(app) {
  const line1 = app.currentAddress || app.propertyAddress || '';
  const city  = app.currentCity || app.propertyCity || '';
  const state = app.currentState || app.propertyState || '';
  const parts = [line1, [city, state].filter(Boolean).join(', ')].filter(Boolean);
  return parts.join(' · ') || '—';
}
function formatNoteWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Kinfolk operates out of Arizona — pin all dialer activity timestamps
  // to America/Phoenix so an LO's laptop-timezone (or a mis-set browser
  // clock) never makes the feed disagree with what they actually said
  // on the call. Phoenix doesn't observe DST, so this is always MST.
  const TZ = 'America/Phoenix';
  // Same-day check must also honour Phoenix — a call logged at 11:30 PM
  // local can be "yesterday" in Phoenix or vice-versa. `en-CA` gives us a
  // reliable YYYY-MM-DD shape for direct string comparison.
  const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ });
  const sameDay = dayFmt.format(d) === dayFmt.format(new Date());
  if (sameDay) {
    return `Today ${d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' })}`;
  }
  return d.toLocaleString('en-US', { timeZone: TZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
